// Players, capture counts, pulsing turn indicator, move counter, status badges.

import type { GameDetail } from '../../shared/types.ts';
import { useStore } from '../store/useStore.ts';

function playerName(detail: GameDetail, color: 'B' | 'W'): string {
  if (detail.game.mode === 'hotseat') return color === 'B' ? 'Black' : 'White';
  if (detail.game.humanColor === color) return 'You';
  return `GNU Go · L${detail.game.botLevel ?? '?'}`;
}

export interface GameInfoStripProps {
  viewedTurn: number;
  captures: { B: number; W: number };
  toPlay: 'B' | 'W';
}

export function GameInfoStrip({ viewedTurn, captures, toPlay }: GameInfoStripProps) {
  const detail = useStore((s) => s.detail);
  const cursor = useStore((s) => s.cursor);
  const botThinking = useStore((s) => s.botThinking);
  const health = useStore((s) => s.health);
  if (!detail) return null;

  const total = detail.moves.length;
  const status = detail.game.status;
  const active = status === 'active';

  const side = (color: 'B' | 'W') => {
    const isTurn = active && toPlay === color && cursor === null;
    return (
      <div className={`player-side ${isTurn ? 'is-turn' : ''}`}>
        <span className={`stone-icon ${color === 'B' ? 'stone-icon-b' : 'stone-icon-w'}`} aria-hidden="true" />
        <div className="player-meta">
          <span className="player-name">{playerName(detail, color)}</span>
          <span className="player-caps">
            {captures[color]} {captures[color] === 1 ? 'capture' : 'captures'}
          </span>
        </div>
        {isTurn && (
          <span className="turn-dot" title={`${color === 'B' ? 'Black' : 'White'} to play`}>
            <span className="sr-only">to play</span>
          </span>
        )}
      </div>
    );
  };

  return (
    <header className="info-strip">
      <div className="info-players">
        {side('B')}
        <span className="info-vs" aria-hidden="true">
          ·
        </span>
        {side('W')}
      </div>
      <div className="info-status">
        <span className="move-counter mono">
          {cursor !== null ? `move ${viewedTurn} / ${total}` : `move ${total}`}
        </span>
        {botThinking && <span className="badge badge-thinking">thinking…</span>}
        {status === 'scoring' && <span className="badge badge-scoring">scoring</span>}
        {status === 'finished' && (
          <span className="badge badge-finished">{detail.game.result ?? 'finished'}</span>
        )}
        {health?.katago === 'starting' && (
          <span className="badge badge-warming" title="KataGo is warming up; analysis is briefly unavailable">
            KataGo starting
          </span>
        )}
        {health?.katago === 'down' && (
          <span className="badge badge-down" title="KataGo is not running; analysis unavailable">
            KataGo down
          </span>
        )}
      </div>
    </header>
  );
}
